import type { ReservationSpanPaymentLink } from '@repo/domain';
import { differenceInMinutes, formatDate, formatDuration } from '@repo/date-time';
import { formatFlatPrice } from '@repo/domain-service';
import { i18n, tr } from '@repo/locale';
import { type TestId } from '@repo/test-id';
import { Button } from '@repo/ui';
import { type ReactNode } from 'react';

import Header from '@features/reservation/components/Header';

import PageFrame from '@shared/components/page/PageFrame';

interface Props {
  testId: TestId;
  link: ReservationSpanPaymentLink;
  /** The renter signed in gets RESERVATION DETAILS in place of LOG IN. */
  isRenter: boolean;
  loginButtonLabel: string;
  /** The login, shown over the page while it is asked for. */
  drawer: ReactNode;
  onPayNow: () => void;
  onLogin: () => void;
  onShowReservation: () => void;
}

/** The span the link pays for — its parking, period and price alone — with PAY NOW and LOG IN or RESERVATION DETAILS. */
export default function SpanPaymentLinkStartContents({
  testId,
  link,
  isRenter,
  loginButtonLabel,
  drawer,
  onPayNow,
  onLogin,
  onShowReservation,
}: Props) {
  const handlePayNow = () => {
    onPayNow();
  };

  const handleLogin = () => {
    onLogin();
  };

  const handleShowReservation = () => {
    onShowReservation();
  };

  return (
    <PageFrame testId={testId} drawer={drawer}>
      <Header
        title={tr('reservations:span_payment_link.title')}
        subtitle={tr('reservations:span_payment_link.subtitle')}
        testId={testId.append('title')}
      />
      {/* The paid period ends where the unpaid span starts. */}
      {link.startAt && (
        <div className="text-heading flex flex-col" data-testid={testId.append('expiresAt')}>
          <p className="font-semibold">{tr('reservations:span_payment_link.expires_at')}</p>
          <p>
            {tr('reservations:span_payment_link.expires_at_date', {
              date: formatDate(link.startAt, true),
              timeLeft: formatDuration(Math.max(0, differenceInMinutes(link.startAt, new Date()))),
            })}
          </p>
        </div>
      )}
      <div className="bg-primary flex flex-col gap-2 rounded-3xl p-4" data-testid={testId.append('summary')}>
        {link.parkingName && <p className="text-heading text-base font-semibold">{link.parkingName}</p>}
        {link.startAt && link.endAt && (
          <p className="text-body text-sm">
            {tr('reservations:span_payment_link.period', {
              start: formatDate(link.startAt, true),
              end: formatDate(link.endAt, true),
              duration: formatDuration(differenceInMinutes(link.endAt, link.startAt)),
            })}
          </p>
        )}
        {link.price && (
          <p className="text-heading text-lg font-semibold">{formatFlatPrice(link.price, i18n.language)}</p>
        )}
      </div>
      <div className="mt-4 flex gap-3">
        {isRenter ? (
          <Button
            color="secondary"
            size="lg"
            className="flex-1"
            label={tr('reservations:span_payment_link.reservation_details')}
            testId={testId.append('reservationDetailsButton')}
            onClick={handleShowReservation}
          />
        ) : (
          <Button
            color="secondary"
            size="lg"
            className="flex-1"
            label={loginButtonLabel}
            testId={testId.append('loginButton')}
            onClick={handleLogin}
          />
        )}
        <Button
          color="brand"
          size="lg"
          className="flex-1"
          label={tr('reservations:span_payment_link.pay_now')}
          testId={testId.append('payNowButton')}
          onClick={handlePayNow}
        />
      </div>
    </PageFrame>
  );
}
